import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import {
  createPriorityList,
  deletePriorityListById,
  deletePriorityListsByIds,
  getPriorityListByIdExpanded,
  getPriorityLists,
  priorityListFromRSOs,
  setActiveForAstrosites,
  setPriorityListDescription,
  setPriorityListName,
  setPriorityListNoradIds,
  setPriorityListPriority,
} from "./api"
import { allowUpdate } from "../../app/store"
import { parseIDs } from "../rsos/parse"

const initialState = {
  byId: {}, // A map of priority lists by id
  matchesByCtx: {
    // root context powering the priority list view
    root: {
      _loadStatus: "idle", // Information about the loading status of a call
      _errorMessage: undefined,
      ids: [], // priority lists by id
      page: {}, // Pagination information
    },
    /*
              Example of other contexts
              "rso/123": {
                ids: [],
                page: {}, // Pagination information
              },
            */
  },
  creating: {}, // space for new priority lists that are in the process of being created
  deleting: {}, // priority lists that will be deleted on submission
}

/**
 * Thunk to perform async logic to fetch the priority lists matching the provided filters
 *
 * @type {AsyncThunk<*, {readonly filter?: *, readonly context?: *, readonly page?: *}, {}>}
 */
export const fetchPriorityLists = createAsyncThunk(
  "priorities/fetch",
  /**
   * @param filter the filters to apply to the priorities search
   * @param page Pagination information
   * @param context The context from where the query is being made.  If its from the priorities list view then root otherwise examples could be `observation/123`
   * @returns {Promise<*>}
   */
  async ({ filter, page, context }) => {
    const response = await getPriorityLists({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

/**
 * Thunk to perform async logic to fetch a priority list by its ID
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchPriorityListById = createAsyncThunk(
  "priority/fetch",
  async (priorityId) => {
    return await getPriorityListByIdExpanded(priorityId)
  }
)

/**
 * Thunk to perform async logic to create a priority list
 * @type {AsyncThunk<*, void, {}>}
 */
export const addPriorityList = createAsyncThunk(
  "priority/create",
  async (
    { name, description, priority, selectedAstrosites = [], norad_ids },
    { rejectWithValue }
  ) => {
    try {
      // Split the string and make an array
      if (norad_ids.length > 0) {
        norad_ids = norad_ids.split(",")
      }

      return await createPriorityList({
        name,
        description,
        priority,
        active_for_astrosites: selectedAstrosites,
        norad_ids,
      })
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Thunk to perform async logic to create a priority list from RSO's matching the provided filters
 *
 * @type {AsyncThunk<*, {readonly filter?: *, readonly context?: *, readonly page?: *}, {}>}
 */
export const addPriorityListsFromMatchingRSOs = createAsyncThunk(
  "priority/create/rsos",
  /**
   * @param filter the filters to apply to the priorities search
   * @param listName string name of new list
   * @param listDescription string description of new list
   * @param listPriority number priority of new list
   * @param listEnabled bool is ne list enabled
   * @param context The context from where the query is being made.  If its from the priorities list view then root otherwise examples could be `observation/123`
   * @returns {Promise<*>}
   */
  async ({
    filter = {},
    name,
    description,
    priority = 0,
    selectedAstrosites = [],
    list_id,
  }) => {
    const response = await priorityListFromRSOs({
      filter,
      name,
      description,
      priority,
      active_for_astrosites: selectedAstrosites,
      list_id,
    })

    return response
  }
)

/**
 * Thunk to perform async logic to set the active state of a priority list to true or false
 * @type {AsyncThunk<*, void, {}>}
 */
export const updatePriorityListActiveFor = createAsyncThunk(
  "priority/update/active",
  async ({ priority_id, astrosite_ids, _etag }, { rejectWithValue }) => {
    try {
      return await setActiveForAstrosites({
        priority_id,
        astrosite_ids,
        _etag,
      })
    } catch (error) {
      throw rejectWithValue(error.statusText)
    }
  }
)

/**
 * Thunk to perform async logic to set the priority of a priority list
 * @type {AsyncThunk<*, void, {}>}
 */
export const updatePriorityListPriority = createAsyncThunk(
  "priority/update/priority",
  async ({ priority_id, priority, _etag }, { rejectWithValue }) => {
    try {
      return await setPriorityListPriority({ priority_id, priority, _etag })
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Thunk to perform async logic to set the name of a priority list
 * @type {AsyncThunk<*, void, {}>}
 */
export const updatePriorityListName = createAsyncThunk(
  "priority/update/name",
  async ({ priority_id, name, _etag }, { rejectWithValue }) => {
    try {
      return await setPriorityListName({ priority_id, name, _etag })
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Thunk to perform async logic to set the description of a priority list
 * @type {AsyncThunk<*, void, {}>}
 */
export const updatePriorityListDescription = createAsyncThunk(
  "priority/update/description",
  async ({ priority_id, description, _etag }, { rejectWithValue }) => {
    try {
      return await setPriorityListDescription({
        priority_id,
        description,
        _etag,
      })
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Thunk to perform async logic to delete a priority list by its ID
 * @type {AsyncThunk<*, void, {}>}
 */
export const removePriorityListById = createAsyncThunk(
  "priority/remove",
  async (priorityId) => {
    return await deletePriorityListById(priorityId)
  }
)

/**
 * Thunk to perform async logic to delete priority lists by their IDs
 * @type {AsyncThunk<*, void, {}>}
 */
export const removePriorityListsByIds = createAsyncThunk(
  "priorities/remove",
  async (priorityIds) => {
    return await deletePriorityListsByIds(priorityIds)
  }
)

/** Replace the objects (norad_ids) in a list with a new set.
 * Called after a user has made changes to a list. */
export const setPriorityListObjects = createAsyncThunk(
  "priorities/set-objects",
  async ({ priority_id = "", norad_ids = [], _etag }, { rejectWithValue }) => {
    try {
      return await setPriorityListNoradIds({
        priority_id,
        norad_ids,
        _etag,
      })
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Priorities reducer
 */
export const prioritiesSlice = createSlice({
  name: "priorities",
  initialState,
  reducers: {
    // Remove `itemIDs` from list `listID` in the locally stored list,
    // setting a modified flag.
    removeFromList: (state, { payload: { listID, itemIDs } }) => {
      if (!listID || !itemIDs || !state.byId[listID]) {
        return
      }

      const toRemove = new Set(itemIDs)
      state.byId[listID].norad_ids = state.byId[listID].norad_ids.filter(
        (id) => !toRemove.has(id)
      )
      state.byId[listID]._modified = true
    },

    // Add a string representing one or more NORAD IDs to the list.
    // This action creator finds all the IDs in the given string and
    // avoids adding duplicates.
    addToList: (state, { payload: { listID, idString } }) => {
      if (!listID || !idString || !state.byId[listID]) {
        return
      }

      const allIDs = new Set(state.byId[listID].norad_ids)
      // Convert idString into a list of sanitised NoradIDs which
      // are not already in the list
      const toAdd = parseIDs(idString).filter((id) => !allIDs.has(id))
      // Add the remainder to the priority list (also avoiding duplicates internally)
      state.byId[listID].norad_ids = [
        ...state.byId[listID].norad_ids,
        ...new Set(toAdd),
      ]
      state.byId[listID]._modified = true
    },

    // Replace the ID at a given index with a new one. If the new ID is already present
    // in the given priority list, treat instead as a *delete* of the item at this index.
    replaceIDAtIndex: (
      state,
      { payload: { listID = "", index = -1, newItemID = 0 } }
    ) => {
      if (!listID || !state.byId[listID] || index < 0 || !newItemID) {
        return
      }

      // Does our list already contain this ID at a different location? If
      // so, remove the requested index.
      const existing = state.byId[listID].norad_ids
      const alreadyAt = existing.indexOf(newItemID)
      if (alreadyAt != index && alreadyAt >= 0) {
        state.byId[listID].norad_ids = [
          ...existing.slice(0, index),
          ...existing.slice(index + 1),
        ]
      } else {
        state.byId[listID].norad_ids[index] = newItemID
      }
      state.byId[listID]._modified = true
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPriorityListById.pending, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._loadStatus = "loading"
      })
      .addCase(fetchPriorityListById.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          // Note: This payload also optionally includes RSO data which
          // is handled in rsos/slice.js
          if (
            allowUpdate(
              state.byId[action.payload.id]
                ? state.byId[action.payload.id]._at
                : undefined,
              action.payload._at
            )
          ) {
            state.byId[action.payload.id] = action.payload
            state.byId[action.payload.id]._loadStatus = "idle"
          }
        }
      })
      .addCase(fetchPriorityListById.rejected, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._loadStatus = "rejected"
        state.byId[action.meta.arg]._errorMessage += action.payload
      })
      .addCase(addPriorityList.pending, (state) => {
        if (!state["creating"]) {
          state["creating"] = {}
        }
        state["creating"]._saveStatus = "creating"
      })
      .addCase(addPriorityList.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          action.payload.object_count = action.payload.norad_ids
            ? action.payload.norad_ids.length
            : 0
          state.byId[action.payload.id] = action.payload
          state.matchesByCtx.root.ids.push(action.payload.id)
        }
        state["creating"]._saveStatus = "idle"
      })
      .addCase(addPriorityList.rejected, (state, action) => {
        state["creating"]._saveStatus = "rejected"
        state["creating"]._errorMessage = action.payload?.error
      })
      .addCase(removePriorityListById.pending, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._saveStatus = "deleting"
      })
      .addCase(removePriorityListById.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          // state.byId[action.payload.id] = action.payload
          state.matchesByCtx.root.ids.filter((id) => action.payload.id != id)
          action.payload.ids?.map(
            (id) => (state.byId[id] = { id, deleted: true })
          )
        }
      })
      .addCase(removePriorityListById.rejected, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._saveStatus = "rejected"
        state.byId[action.meta.arg]._errorMessage += action.payload
      })
      .addCase(removePriorityListsByIds.pending, (state) => {
        if (!state["deleting"]) {
          state["deleting"] = {}
        }
        state["deleting"]._saveStatus = "deleting"
      })
      .addCase(removePriorityListsByIds.fulfilled, (state, action) => {
        if (action?.payload?.ids) {
          action.payload.ids?.map(
            (id) => (state.byId[id] = { id, deleted: true })
          )
        }
        state["deleting"]._saveStatus = "idle"
      })
      .addCase(removePriorityListsByIds.rejected, (state, action) => {
        state["deleting"]._saveStatus = "rejected"
        state["deleting"]._errorMessage += action.payload
      })
      .addCase(fetchPriorityLists.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.matchesByCtx[context]) {
          state.matchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.matchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchPriorityLists.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (state.matchesByCtx[context]) {
          state.matchesByCtx[context]._loadStatus = "idle"
          if (allowUpdate(state.matchesByCtx[context].at, action.payload.at)) {
            // Don't reset if we are paging
            if (action?.payload?.page?.start_index === 0) {
              state.matchesByCtx[context].ids = []
            }
            action?.payload?.data?.map((priority) => {
              // Add the priotity lists to state by id (this can include ids not retrieved in this call)
              state.byId[priority.id] = {
                ...priority,
                _at: action.payload.at,
              }
              // add the ids of the priorities that match from this query
              if (state.matchesByCtx[context].ids.indexOf(priority.id) === -1) {
                state.matchesByCtx[context].ids.push(priority.id)
              }
            })
            state.matchesByCtx[context].page = action.payload.page
            state.matchesByCtx[context].at = action.payload.at
          }
        }
      })
      .addCase(fetchPriorityLists.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.matchesByCtx[context]._loadStatus = "rejected"
        state.matchesByCtx[context]._errorMessage = action?.error?.message
      })
      .addCase(updatePriorityListActiveFor.pending, (state, action) => {
        if (!state.byId[action.meta.arg?.priority_id]) {
          state.byId[action.meta.arg?.priority_id] = {}
        }
        state.byId[action.meta.arg?.priority_id]._saveStatus = "updating"
        // Optimistic local update so UI displays new values
        state.byId[action.meta.arg?.priority_id].astrosite_ids =
          action.meta.arg?.astrosite_ids
      })
      .addCase(updatePriorityListActiveFor.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          state.byId[action.payload.id] = action.payload
          state.byId[action.payload.id]._saveStatus = "idle"
        }
      })
      .addCase(updatePriorityListActiveFor.rejected, (state, action) => {
        if (!state.byId[action.meta.arg?.priority_id]) {
          state.byId[action.meta.arg?.priority_id] = {}
        }
        state.byId[action.meta.arg?.priority_id]._saveStatus = "rejected"
        state.byId[action.meta.arg?.priority_id]._errorMessage = action.payload
      })
      .addCase(updatePriorityListPriority.pending, (state, action) => {
        if (!state.byId[action.meta.arg?.priority_id]) {
          state.byId[action.meta.arg?.priority_id] = {}
        }
        state.byId[action.meta.arg?.priority_id]._saveStatus = "updating"
      })
      .addCase(updatePriorityListPriority.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          state.byId[action.payload.id] = action.payload
          state.byId[action.payload.id]._saveStatus = "idle"
        }
      })
      .addCase(updatePriorityListPriority.rejected, (state, action) => {
        if (!state.byId[action.meta.arg?.priority_id]) {
          state.byId[action.meta.arg?.priority_id] = {}
        }
        state.byId[action.meta.arg?.priority_id]._saveStatus = "rejected"
        state.byId[action.meta.arg?.priority_id]._errorMessage += action.payload
      })
      .addCase(updatePriorityListName.pending, (state, action) => {
        if (!state.byId[action.meta.arg?.priority_id]) {
          state.byId[action.meta.arg?.priority_id] = {}
        }
        state.byId[action.meta.arg?.priority_id]._saveStatus = "updating"
      })
      .addCase(updatePriorityListName.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          state.byId[action.payload.id] = action.payload
          state.byId[action.payload.id]._saveStatus = "idle"
        }
      })
      .addCase(updatePriorityListName.rejected, (state, action) => {
        if (!state.byId[action.meta.arg?.priority_id]) {
          state.byId[action.meta.arg?.priority_id] = {}
        }
        state.byId[action.meta.arg?.priority_id]._saveStatus = "rejected"
        state.byId[action.meta.arg?.priority_id]._errorMessage += action.payload
      })
      .addCase(updatePriorityListDescription.pending, (state, action) => {
        if (!state.byId[action.meta.arg?.priority_id]) {
          state.byId[action.meta.arg?.priority_id] = {}
        }
        state.byId[action.meta.arg?.priority_id]._saveStatus = "updating"
      })
      .addCase(updatePriorityListDescription.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          state.byId[action.payload.id] = action.payload
          state.byId[action.payload.id]._saveStatus = "idle"
        }
      })
      .addCase(updatePriorityListDescription.rejected, (state, action) => {
        if (!state.byId[action.meta.arg?.priority_id]) {
          state.byId[action.meta.arg?.priority_id] = {}
        }
        state.byId[action.meta.arg?.priority_id]._saveStatus = "rejected"
        state.byId[action.meta.arg?.priority_id]._errorMessage += action.payload
      })
      .addCase(addPriorityListsFromMatchingRSOs.pending, (state) => {
        if (!state["creating"]) {
          state["creating"] = {}
        }
        state["creating"]._saveStatus = "creating"
      })
      .addCase(addPriorityListsFromMatchingRSOs.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          action.payload.object_count = action.payload.norad_ids
            ? action.payload.norad_ids.length
            : 0
          state.byId[action.payload.id] = action.payload
        }
        state["creating"]._saveStatus = "idle"
      })
      .addCase(addPriorityListsFromMatchingRSOs.rejected, (state, action) => {
        state["creating"]._saveStatus = "rejected"
        state["creating"]._errorMessage = action.payload
      })
      // Save changes to a priority list's objects
      .addCase(setPriorityListObjects.pending, (state, action) => {
        state.byId[action.meta.arg?.priority_id]._saveStatus = "updating"
      })
      .addCase(setPriorityListObjects.fulfilled, (state, action) => {
        state.byId[action.payload.id] = action.payload
        state.byId[action.payload.id]._saveStatus = "idle"
        state.byId[action.payload.id]._loadStatus = "idle"
      })
      .addCase(setPriorityListObjects.rejected, (state, action) => {
        state.byId[action.meta.arg?.priority_id]._saveStatus = "rejected"
        state.byId[action.meta.arg?.priority_id]._errorMessage = action.payload
      })
  },
})

// Selector for all priority lists
export const selectPriorityListsById = (state) => state.priorities.byId
export const selectCreatingPriorityList = (state) => state.priorities.creating
export const selectDeletingPriorityList = (state) => state.priorities.deleting

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectPriorityListsByContext(state, `rso/${id}`))
 */
export const selectPriorityListsByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.priorities.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`priorities, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectPriorityListsByIds(state, ["1", "2"]))
 */
export const selectPriorityListsByIds = createSelector(
  [
    // first input - extract value from `state`
    selectPriorityListsById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)

export const { setPriorityList, removeFromList, addToList, replaceIDAtIndex } =
  prioritiesSlice.actions

export default prioritiesSlice.reducer
